import {
  cslProblemSummary,
  recordRevision,
  validateCslItem,
  type AnnotationId,
  type CollectionId,
  type MutationId,
  type Page,
  type ReaderRequestOptions,
  type ReadingPosition,
  type Source,
  type SourceId,
  type SourceQuery,
  type SourceRepository,
  type SourceSummary,
} from "@mdbase-reader/core";

import { sourceContract } from "./contracts.js";
import { sourceFromDocument, sourceSummaryFromQuery } from "./mapping.js";
import {
  ConnectRepositoryError,
  outcomeValue,
  queryWithOptions,
  readWithOptions,
  recordPathById,
  updateWithOptions,
} from "./repository-client.js";
import { matchesSearch, objectValue, positionFrontmatter } from "./source-repository-helpers.js";

import type { ReaderConnectClient } from "./repository-client.js";
import type { QueryRecord } from "@mdbase-dev/connect";

export class ConnectSourceRepository implements SourceRepository {
  readonly #pathsById = new Map<string, string>();

  constructor(private readonly client: ReaderConnectClient) {}

  async list(query: SourceQuery, options: ReaderRequestOptions = {}): Promise<Page<SourceSummary>> {
    const result = outcomeValue(
      await queryWithOptions(
        this.client,
        {
          contract: sourceContract,
          frontmatterMode: "effective",
          limit: query.limit,
          ...(query.cursor ? { cursor: query.cursor } : { pagination: "cursor" as const }),
        },
        options,
      ),
      "query sources",
    );
    const items = this.#sourceItems(query, result.results);
    const hasMore = result.meta?.hasMore ?? false;
    const nextCursor = hasMore ? result.meta?.cursor : undefined;
    if (hasMore && !nextCursor) {
      throw new Error("The collection authority omitted the next source cursor.");
    }
    return {
      items,
      ...(nextCursor ? { nextCursor } : {}),
      ...(typeof result.meta?.totalCount === "number"
        ? { totalCount: result.meta.totalCount }
        : {}),
    };
  }

  async *listPages(
    query: Omit<SourceQuery, "cursor">,
    options: ReaderRequestOptions = {},
  ): AsyncIterable<Page<SourceSummary>> {
    for await (const outcome of this.client.queryPages(
      {
        contract: sourceContract,
        frontmatterMode: "effective",
      },
      {
        ...options,
        firstPageSize: query.limit,
        pageSize: 1_000,
      },
    )) {
      const page = outcomeValue(outcome, "query sources");
      const items = this.#sourceItems(query, page.results);
      yield {
        items,
        ...(page.cursor ? { nextCursor: page.cursor } : {}),
        ...(typeof page.meta?.totalCount === "number" ? { totalCount: page.meta.totalCount } : {}),
      };
    }
  }

  async get(
    collection: CollectionId,
    id: SourceId,
    options: ReaderRequestOptions = {},
  ): Promise<Source | null> {
    const path =
      this.#pathsById.get(id) ?? (await recordPathById(this.client, sourceContract, id, options));
    if (!path) {
      return null;
    }
    this.#pathsById.set(id, path);
    const result = outcomeValue(
      await readWithOptions(this.client, { path, includeDocument: true }, options),
      "read source",
    );
    return sourceFromDocument(collection, result);
  }

  async updateBody(
    input: {
      readonly collectionId: CollectionId;
      readonly sourceId: SourceId;
      readonly expectedRevision: ReturnType<typeof recordRevision>;
      readonly body: string;
    },
    options: ReaderRequestOptions = {},
  ): Promise<Source> {
    const path = await this.#path(input.sourceId, "update source note", options);
    const updated = outcomeValue(
      await updateWithOptions(
        this.client,
        {
          path,
          ifRevision: input.expectedRevision,
          patch: {},
          body: input.body,
        },
        options,
      ),
      "update source note",
    );
    return sourceFromDocument(input.collectionId, updated);
  }

  async updateReading(
    input: {
      readonly collectionId: CollectionId;
      readonly sourceId: SourceId;
      readonly expectedRevision: ReturnType<typeof recordRevision>;
      readonly documentFileId: Parameters<SourceRepository["updateReading"]>[0]["documentFileId"];
      readonly position: ReadingPosition;
      readonly openedAt: Parameters<SourceRepository["updateReading"]>[0]["openedAt"];
    },
    options: ReaderRequestOptions = {},
  ): Promise<Source> {
    const path = await this.#path(input.sourceId, "save reading position", options);
    const current = outcomeValue(
      await readWithOptions(this.client, { path, includeDocument: true }, options),
      "read source before saving position",
    );
    const existing = objectValue(current.frontmatter["reading"]);
    const reading = {
      ...existing,
      status: typeof existing["status"] === "string" ? existing["status"] : "reading",
      document_file_id: input.documentFileId,
      position: positionFrontmatter(input.position),
      started_at: existing["started_at"] ?? input.openedAt,
      last_opened_at: input.openedAt,
    };
    const updated = outcomeValue(
      await updateWithOptions(
        this.client,
        {
          path,
          ifRevision: recordRevision(current.revision),
          patch: { reading },
          includeDocument: true,
        },
        options,
      ),
      "save reading position",
    );
    return sourceFromDocument(input.collectionId, updated);
  }

  async updateCitation(
    input: Parameters<SourceRepository["updateCitation"]>[0],
    options: ReaderRequestOptions = {},
  ): Promise<Source> {
    const validation = validateCslItem(input.citation);
    if (!validation.valid) {
      throw new ConnectRepositoryError(
        "save citation metadata",
        cslProblemSummary(validation.problems),
      );
    }
    const path = await this.#path(input.sourceId, "save citation metadata", options);
    const updated = outcomeValue(
      await updateWithOptions(
        this.client,
        {
          path,
          ifRevision: input.expectedRevision,
          patch: { csl: validation.item },
          includeDocument: true,
        },
        options,
      ),
      "save citation metadata",
    );
    return sourceFromDocument(input.collectionId, updated);
  }

  async appendAnnotationEmbed(
    input: {
      readonly collectionId: CollectionId;
      readonly sourceId: SourceId;
      readonly expectedRevision: ReturnType<typeof recordRevision>;
      readonly annotationId: AnnotationId;
      readonly embed: string;
      readonly idempotencyKey: MutationId;
    },
    options: ReaderRequestOptions = {},
  ): Promise<ReturnType<typeof recordRevision>> {
    const path = await this.#path(input.sourceId, "append annotation", options);
    const current = outcomeValue(
      await readWithOptions(this.client, { path, includeDocument: true }, options),
      "read source before annotation",
    );
    const body = current.body ?? "";
    if (body.includes(input.embed)) {
      return recordRevision(current.revision);
    }
    const separator = body.length === 0 || body.endsWith("\n") ? "" : "\n";
    const updated = outcomeValue(
      await updateWithOptions(
        this.client,
        {
          path,
          ifRevision: input.expectedRevision,
          patch: {},
          body: `${body}${separator}\n${input.embed}\n`,
        },
        options,
      ),
      "append annotation",
    );
    return recordRevision(updated.revision);
  }

  async #path(
    id: SourceId,
    operation: string,
    options: ReaderRequestOptions = {},
  ): Promise<string> {
    const path =
      this.#pathsById.get(id) ?? (await recordPathById(this.client, sourceContract, id, options));
    if (!path) {
      throw new ConnectRepositoryError(operation, "source_not_found");
    }
    this.#pathsById.set(id, path);
    return path;
  }

  #sourceItems(
    query: Omit<SourceQuery, "cursor">,
    records: readonly QueryRecord[],
  ): SourceSummary[] {
    return records
      .map((record) => sourceSummaryFromQuery(query.collectionId, record))
      .map((source) => {
        this.#pathsById.set(source.id, source.path);
        return source;
      })
      .filter(
        (source) =>
          query.readingStatus === undefined || source.readingStatus === query.readingStatus,
      )
      .filter((source) => matchesSearch(source, query.search));
  }
}
